import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import Component from '@glimmer/component';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
import t from 'ember-intl/helpers/t';
import RouteTemplate from 'ember-route-template';
import set from 'ember-set-helper/helpers/set';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import AdditionalGuestsField from 'invite-sign/components/additional-guests-field';
import MappedField from 'invite-sign/components/mapped-field';
import type SignInField from 'invite-sign/models/sign-in-field';
import type { GroupInviteLinkShowFillRouteModel } from 'invite-sign/routes/group-invite/show/fill';

interface GroupInviteLinkShowFillComponentSignature {
  Args: {
    model: GroupInviteLinkShowFillRouteModel;
  };
}

class GroupInviteLinkShowFillComponent extends Component<GroupInviteLinkShowFillComponentSignature> {
  @service declare router: RouterService;

  get mappedFields(): SignInField[] {
    const signInFields = this.args.model?.registration?.signInFields || [];

    return signInFields.filter((field) => field.selfServiceable);
  }

  onAdditionalGuestCountChange = (guestCount: number): void => {
    if (!this.args.model.registration) return;
    this.args.model.registration.additionalGuests = guestCount;
  };

  saveTask = task(
    { drop: true },
    waitFor(async () => {
      const { groupInviteLink, registration } = this.args.model;

      if (!registration || !registration.hasValidSignInFields) return;

      try {
        const savedInvite = await registration.save();
        this.router.transitionTo('invite.show.group-landing', savedInvite.id);
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error('Failed to save invite:', error);

        if (this.#isCapacityLimitError(error)) {
          // Update the model to reflect that capacity has been reached (as the API error indicates)
          if (groupInviteLink) groupInviteLink.remainingCapacity = 0;
          await this.router.replaceWith('group-invite.show.exceeded');
          return;
        }

        await this.router.replaceWith('try-again');
      }
    }),
  );

  #isCapacityLimitError = (error: unknown): boolean => {
    if (error && typeof error === 'object') {
      const errorObj = error as { meta?: { message?: string } };
      return errorObj.meta?.message?.toLowerCase().includes('capacity limit') ?? false;
    }
    return false;
  };

  <template>
    <h1 data-test-header-text class="mb-2 text-2xl">{{t "group_invite.add_your_details"}}</h1>
    <div data-test-subheader-text class="text-sm mb-8 text-grey-darkest md:text-base">{{t
        "invite.start_by_confirming_details"
      }}.</div>

    <div class="antialiased" data-test-field="Your Full Name">
      <div class="mb-2" data-test-label>
        <label class="leading-tight grayDarker text-base" for="group-invite-registration-full-name">
          {{t "visitor.full_name"}}
          <span class="requiredField"></span>
        </label>
      </div>
      <input
        data-test-full-name-input
        type="text"
        id="group-invite-registration-full-name"
        class="inputClass rounded-md"
        value={{@model.registration.fullName}}
        {{on "input" (pick "target.value" (set @model.registration "fullName"))}}
      />
    </div>

    <div class="antialiased" data-test-field="Your Email Address">
      <div class="mb-2" data-test-label>
        <label class="leading-tight grayDarker text-base" for="group-invite-registration-email">
          {{! The only in-app translation for email address is from "protect" }}
          {{t "protect.email_address"}}
          <span class="requiredField"></span>
        </label>
      </div>
      <input
        data-test-email-input
        type="email"
        id="group-invite-registration-email"
        class="inputClass rounded-md"
        value={{@model.registration.email}}
        {{on "input" (pick "target.value" (set @model.registration "email"))}}
      />
    </div>

    <div>
      {{#each this.mappedFields as |mappedField|}}
        {{#if mappedField.isVisible}}
          <MappedField @mappedField={{mappedField}} @invite={{@model.registration}} />
        {{/if}}
      {{/each}}
    </div>

    {{#if @model.groupInviteLink.additionalGuestsEnabled}}
      <div data-test-additional-guests-field class="mt-4">
        <AdditionalGuestsField
          @guestCount={{@model.registration.additionalGuests}}
          @onGuestCountChange={{this.onAdditionalGuestCountChange}}
        />
      </div>
    {{/if}}

    <button
      data-test-submit
      data-synthetic="next-btn"
      type="button"
      style="border-radius: 3px; min-height:44px;"
      class="{{if this.saveTask.isRunning 'disabled'}}
        shadow hover:shadow-md float-right mt-3 btn btn--accent btn--expand mb-6 relative transition"
      disabled={{this.saveTask.isRunning}}
      {{on "click" this.saveTask.perform}}
    >
      {{#if this.saveTask.isRunning}}
        {{svgJar "progress-loading" class="absolute loading-spinner"}}
      {{else}}
        {{t "buttons.next"}}
        {{svgJar "arrow-right" class="inline"}}
      {{/if}}
    </button>

    <a class="envoyLogoFooter" href="https://envoy.com/" aria-label="Envoy home"></a>
  </template>
}

export default RouteTemplate(GroupInviteLinkShowFillComponent);
